import { useCallback } from "react";
import { deleteChat as deleteChatFromDB, updatePinStatus } from "instantdb/client/chat";
import { useChats } from "@/hooks/useChats";

type UseChatActionsReturn = {
  deleteChat: (chatId: string) => Promise<{ wasActive: boolean; nextChatId: string | null } | null>;
  togglePinChat: (chatId: string) => Promise<void>;
};

interface UseChatActionsProps {
  currentChatId?: string;
}

export const useChatActions = ({
  currentChatId,
}: UseChatActionsProps = {}): UseChatActionsReturn => {
  const { chats } = useChats();

  const deleteChat = useCallback(
    async (targetChatId: string) => {
      try {
        // Check if this is the active chat
        const wasActive = currentChatId === targetChatId;

        // Find the next chat to navigate to if we're deleting the active one
        let nextChatId: string | null = null;
        if (wasActive && chats.length > 1) {
          const currentIndex = chats.findIndex((chat) => chat.id === targetChatId);
          // Try to navigate to the next chat, or the previous one if this was the last
          if (currentIndex < chats.length - 1) {
            nextChatId = chats[currentIndex + 1].id;
          } else if (currentIndex > 0) {
            nextChatId = chats[currentIndex - 1].id;
          }
        }

        // Delete the chat from the database
        await deleteChatFromDB(targetChatId);

        return { wasActive, nextChatId };
      } catch (error) {
        console.error("Error deleting chat:", error);
        return null;
      }
    },
    [currentChatId, chats],
  );

  const togglePinChat = useCallback(
    async (targetChatId: string) => {
      try {
        const chat = chats.find((c) => c.id === targetChatId);
        if (chat) {
          await updatePinStatus(targetChatId, !chat.isPinned);
        }
      } catch (error) {
        console.error("Error toggling pin status:", error);
      }
    },
    [chats],
  );

  return {
    deleteChat,
    togglePinChat,
  };
};

